export type Palette = {
  id: string;
  name: string;
  desc: string;
  colors: string[];
};

export const PALETTE_PRESETS: Palette[] = [
  {
    id: 'fresh',
    name: 'Fresh',
    desc: 'balanceada, multiuso',
    colors: [
      '#5CB377',
      '#D4B15C',
      '#6F88D3',
      '#A88BD7',
      '#E26676',
      '#3FBFB1',
      '#F08A5D',
      '#7DB0FF',
      '#C49ADB',
      '#A7AD56',
    ],
  },
  {
    id: 'cool',
    name: 'Cool',
    desc: 'fríos, azules y violetas',
    colors: [
      '#5B87CC',
      '#8FBEEC',
      '#A88BD7',
      '#C16EAC',
      '#6BD0CC',
      '#7DA8E6',
      '#9170D8',
      '#5AB3D9',
      '#C5A3E0',
      '#6E91B5',
    ],
  },
  {
    id: 'warm',
    name: 'Warm',
    desc: 'cálidos, ámbar y coral',
    colors: [
      '#F0C275',
      '#FFB07A',
      '#E08B61',
      '#CE6F6F',
      '#E26676',
      '#D49A5C',
      '#C97A4C',
      '#E2A06E',
      '#B58E5E',
      '#A86147',
    ],
  },
  {
    id: 'mono',
    name: 'Mono',
    desc: 'escala de grises',
    colors: [
      '#EFEFEF',
      '#C8C8C8',
      '#A8A8A8',
      '#888888',
      '#6E6E6E',
      '#585858',
      '#444444',
      '#9A9A9A',
      '#7A7A7A',
      '#B8B8B8',
    ],
  },
  {
    id: 'forest',
    name: 'Forest',
    desc: 'tonos terrosos',
    colors: [
      '#7BB67A',
      '#A7AD56',
      '#D5B25C',
      '#A48156',
      '#7B5C3E',
      '#5C8D5E',
      '#8FA76A',
      '#C29A4F',
      '#8B6E45',
      '#6A8B6C',
    ],
  },
  {
    id: 'dusk',
    name: 'Dusk',
    desc: 'atardecer',
    colors: [
      '#8B6FE3',
      '#E5689C',
      '#6BD0CC',
      '#F4C97A',
      '#5B87CC',
      '#B4729E',
      '#7A91D9',
      '#E89D7C',
      '#9E8AD9',
      '#D88FB1',
    ],
  },
  {
    id: 'print',
    name: 'Print',
    desc: 'editorial impreso',
    colors: [
      '#C2BDB1',
      '#B89F7A',
      '#7B8073',
      '#A06B5A',
      '#D3CDC2',
      '#9D8F70',
      '#8C7159',
      '#A8A294',
      '#736D5F',
      '#BBA88F',
    ],
  },
];

// Sans options drive --font-sans (body text, navigation, most UI).
export const SANS_FONT_OPTIONS = ['Geist', 'Roboto', 'System'] as const;
export type SansFontOption = (typeof SANS_FONT_OPTIONS)[number];

// Serif options drive --font-serif (the italic "observando" + display titles).
export const SERIF_FONT_OPTIONS = ['Instrument Serif', 'Merriweather', 'Caveat', 'System'] as const;
export type SerifFontOption = (typeof SERIF_FONT_OPTIONS)[number];

// Mono options drive --font-mono (numbers in KPIs, chip codes, hex values).
export const MONO_FONT_OPTIONS = [
  'JetBrains Mono',
  'Source Code Pro',
  'Fira Code',
  'IBM Plex Mono',
  'System',
] as const;
export type MonoFontOption = (typeof MONO_FONT_OPTIONS)[number];

// Back-compat alias: older code referenced FONT_OPTIONS / FontOption for the sans pick.
export const FONT_OPTIONS = SANS_FONT_OPTIONS;
export type FontOption = SansFontOption;

export const DEFAULT_PALETTE_ID = 'fresh';
export const DEFAULT_FONT: SansFontOption = 'Geist';
export const DEFAULT_SERIF: SerifFontOption = 'Instrument Serif';
export const DEFAULT_MONO: MonoFontOption = 'JetBrains Mono';

const FALLBACK_COLORS: string[] = [
  '#5CB377',
  '#D4B15C',
  '#6F88D3',
  '#A88BD7',
  '#E26676',
  '#3FBFB1',
  '#F08A5D',
  '#7DB0FF',
  '#C49ADB',
  '#A7AD56',
];

export function resolvePaletteColors(paletteId: string, customColors?: string[]): string[] {
  if (paletteId === 'custom' && customColors && customColors.length > 0) return customColors;
  const preset = PALETTE_PRESETS.find((p) => p.id === paletteId);
  return preset?.colors ?? FALLBACK_COLORS;
}
